import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { ReactiveJsonRoot } from '@ea-lab/reactive-json';
import { buildDataOverride } from './urlDataOverride';
import NotFoundPage from './NotFoundPage';

// Libellé humain de l'entité exigée par une route (cf. `requireEntity` dans
// config/routes.js). Un type non listé retombe sur « ressource ».
const ENTITY_LABELS = {
  'group/project': 'projet',
  'group/site': 'site',
  'node/task': 'tâche',
};

// Message affiché quand `requireEntityField` n'est pas satisfait. Clé = la clé
// decoupled exigée par la route. Un champ non listé retombe sur un texte neutre.
const MISSING_FIELD_MESSAGES = {
  projectId: (label) => `Cette ${label} n'est rattachée à aucun projet. Ouvrez-la depuis son projet.`,
};

/**
 * Wrapper around ReactiveJsonRoot that fetches the YAML as raw text and seeds
 * the reactive-json store from the URL (path id + query params) via
 * `dataOverride`, so pages read `~~.id` / `~~.wsid` etc. natively — no text
 * token substitution.
 *
 * Deux garde-fous déclarés par la route (config/routes.js) :
 *   - `requireId` : la page n'a aucun sens sans cible (un kanban sans projet).
 *   - `requireEntity` ("group/project"…) : l'id doit désigner une entité de ce
 *     type. Sans lui, /kanban2?id=1234 rendait un board vide MAIS actif, dont le
 *     « + Nouveau » finissait en 422 (field_project pointant un groupe
 *     inexistant) — l'interface promettait ce qu'elle ne pouvait pas tenir.
 *   - `requireEntityField` : cette entité doit avoir le champ renseigné (une
 *     tâche sans projet n'a ni contexte ni droits de groupe).
 */
export default function DynamicYamlPage({ rjbuild, plugins, requireEntity, requireId, requireEntityField }) {
  const params = useParams();
  const location = useLocation();
  const [content, setContent] = useState(null);
  const [dataOverride, setDataOverride] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const id = params.id || searchParams.get('id') || searchParams.get('projectId') || '';

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setPageError(null);
      try {
        // Aucune cible désignée : rien à afficher. (Une route SANS requireId
        // traite l'absence d'id comme un mode création — cf. /task-edit.)
        if (requireId && !id) {
          if (!cancelled) {
            setPageError({ code: 404, reason: 'no-id' });
            setLoading(false);
          }
          return;
        }

        // L'API sépare déjà les deux cas (EntityDecoupledDataController::item) :
        // 404 = inexistant ou mauvais bundle, 403 = existe mais accès refusé.
        // On se contente de la traduire. Même URL que l'additionalDataSource de
        // la page : le batcher /decoupled-api/ regroupe les deux appels.
        if (requireEntity && id) {
          const probe = await fetch(`/decoupled-api/${requireEntity}/${id}?display=decoupled`);
          if (!probe.ok) {
            if (!cancelled) {
              setPageError({ code: probe.status === 403 ? 403 : 404, reason: 'entity' });
              setLoading(false);
            }
            return;
          }

          // Le batcher rend un clone par appelant : lire le corps ici ne prive
          // pas l'additionalDataSource de la page du sien.
          if (requireEntityField) {
            const payload = await probe.json().catch(() => null);
            // La forme dépend des DROITS : decoupled_toolbox enveloppe les champs
            // sous `data` dès que l'utilisateur a l'écriture, et ne le fait pas
            // en lecture seule (la réponse est alors plate). L'enveloppe se
            // reconnaît à `submission_url` — même critère que le serveur (cf.
            // RequestEntity::structureEntityOutput). Lire à la racine faisait
            // échouer la garde pour les utilisateurs les PLUS habilités : 404
            // systématique en admin sur /task-edit.
            const fields = payload?.submission_url ? payload.data : payload;
            if (!fields || !fields[requireEntityField]) {
              if (!cancelled) {
                setPageError({ code: 404, reason: 'field' });
                setLoading(false);
              }
              return;
            }
          }
        }

        const res = await fetch(rjbuild);
        if (!res.ok) throw new Error(`Failed to fetch ${rjbuild}`);
        const text = await res.text();

        if (!cancelled) {
          setContent(text);
          setDataOverride(buildDataOverride(text, location.search, params.id));
        }
      } catch (err) {
        console.error('DynamicYamlPage load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [rjbuild, id, location.search, params.id, requireEntity, requireId, requireEntityField]);

  if (pageError) {
    const label = ENTITY_LABELS[requireEntity] || 'ressource';
    const capitalised = `${label.charAt(0).toUpperCase()}${label.slice(1)}`;

    if (pageError.code === 403) {
      return <NotFoundPage code={403} />;
    }

    const messages = {
      'no-id': `Cette adresse ne désigne aucun ${label}. Passez par la liste pour en ouvrir un.`,
      'entity': `Ce ${label} n'existe pas ou a été supprimé. Vérifiez l'adresse ou revenez à l'accueil.`,
      'field': MISSING_FIELD_MESSAGES[requireEntityField]?.(label)
        ?? `Ce ${label} est incomplet et n'est pas consultable.`,
    };

    return (
      <NotFoundPage
        code={404}
        title={pageError.reason === 'field' ? `${capitalised} inaccessible` : `${capitalised} introuvable`}
        message={messages[pageError.reason]}
      />
    );
  }

  if (loading || !content) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-iris-600" />
      </div>
    );
  }

  return (
    <ReactiveJsonRoot
      key={`${rjbuild}-${id}-${location.search}`}
      maybeRawAppRjBuild={content}
      dataOverride={dataOverride}
      rjBuildFetchMethod="GET"
      plugins={plugins}
    />
  );
}
